import { useEffect, useRef, useState } from 'react';
import { webrtcManager, type CallState } from '../lib/webrtc';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Mic, MicOff, Video, VideoOff, PhoneOff, Users } from 'lucide-react';

interface VideoCallProps {
  roomId: string;
  userId: string;
  onEndCall?: () => void;
}

export default function VideoCall({ roomId, userId, onEndCall }: VideoCallProps) {
  const [callState, setCallState] = useState<CallState>(webrtcManager.getState());
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideosRef = useRef<Map<string, HTMLVideoElement>>(new Map());

  useEffect(() => {
    webrtcManager.setCurrentUser(userId);
    webrtcManager.setCurrentRoom(roomId);

    const unsubscribe = webrtcManager.subscribe((state) => {
      setCallState(state);
    });

    return () => {
      unsubscribe();
    };
  }, [roomId, userId]);

  // Update local video stream
  useEffect(() => {
    if (localVideoRef.current && callState.localStream) {
      localVideoRef.current.srcObject = callState.localStream;
    }
  }, [callState.localStream]);

  // Update remote video streams
  useEffect(() => {
    callState.remoteStreams.forEach((stream, userId) => {
      const videoElement = remoteVideosRef.current.get(userId);
      if (videoElement) {
        videoElement.srcObject = stream;
      }
    });
  }, [callState.remoteStreams]);

  const handleStartCall = async () => {
    try {
      await webrtcManager.joinRoomCall(roomId);
    } catch (error) {
      console.error('Error starting call:', error);
      alert('Failed to start call. Please check your camera and microphone permissions.');
    }
  };

  const handleEndCall = () => {
    webrtcManager.endCall();
    if (onEndCall) {
      onEndCall();
    }
  };

  const handleToggleMute = () => {
    webrtcManager.toggleMute();
  };

  const handleToggleVideo = () => {
    webrtcManager.toggleVideo();
  };

  if (!callState.isInCall) {
    return (
      <Card className="p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            <span className="font-semibold">Voice & Video</span>
          </div>
          <Button onClick={handleStartCall} variant="default">
            Start Call
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4 mb-4">
      <div className="space-y-4">
        {/* Video Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 min-h-[300px]">
          {/* Local Video */}
          <div className="relative bg-black rounded-lg overflow-hidden">
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-2 left-2 bg-black/50 text-white px-2 py-1 rounded text-sm">
              You {callState.isMuted && '(Muted)'} {!callState.isVideoEnabled && '(Camera Off)'}
            </div>
          </div>

          {/* Remote Videos */}
          {Array.from(callState.remoteStreams.entries()).map(([remoteUserId, stream]) => (
            <div key={remoteUserId} className="relative bg-black rounded-lg overflow-hidden">
              <video
                ref={(el) => {
                  if (el) {
                    remoteVideosRef.current.set(remoteUserId, el);
                    el.srcObject = stream;
                  }
                }}
                autoPlay
                playsInline
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-2 left-2 bg-black/50 text-white px-2 py-1 rounded text-sm">
                User {remoteUserId.slice(0, 8)}
              </div>
            </div>
          ))}

          {/* Empty state when no remote streams */}
          {callState.remoteStreams.size === 0 && (
            <div className="flex items-center justify-center bg-muted rounded-lg">
              <div className="text-center text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Waiting for others to join...</p>
              </div>
            </div>
          )}
        </div>

        {/* Call Controls */}
        <div className="flex items-center justify-center gap-4">
          <Button
            onClick={handleToggleMute}
            variant={callState.isMuted ? 'destructive' : 'outline'}
            size="lg"
            className="rounded-full w-14 h-14"
          >
            {callState.isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
          </Button>

          <Button
            onClick={handleToggleVideo}
            variant={!callState.isVideoEnabled ? 'destructive' : 'outline'}
            size="lg"
            className="rounded-full w-14 h-14"
          >
            {callState.isVideoEnabled ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
          </Button>

          <Button
            onClick={handleEndCall}
            variant="destructive"
            size="lg"
            className="rounded-full w-14 h-14"
          >
            <PhoneOff className="h-5 w-5" />
          </Button>
        </div>

        {/* Call Status */}
        <div className="text-center text-sm text-muted-foreground">
          {callState.isCallActive
            ? `Call active • ${callState.remoteStreams.size + 1} participant${callState.remoteStreams.size !== 0 ? 's' : ''}`
            : 'Connecting...'}
        </div>
      </div>
    </Card>
  );
}

